import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PatientCard from '../components/PatientCard';
import { SkeletonList } from '../components/SkeletonLoader';
import EmptyState from '../components/EmptyState';
import QuickStats from '../components/QuickStats';
import { db, savePatients, Patient } from '../lib/db';
import { useNetwork } from '../contexts/NetworkContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { syncManager } from '../lib/syncManager';
import toast from 'react-hot-toast';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export default function PriorityPatients() {
  const { isOnline } = useNetwork();
  const { socket, isConnected } = useWebSocket();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch patients from API (disabled in demo mode)
  const { data: apiPatients, refetch, isLoading } = useQuery({
    queryKey: ['patients'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/patients`);
      return response.data as Patient[];
    },
    enabled: false, // Disabled - using offline data only for demo
    staleTime: 30000,
  });

  // Save to offline storage when fetched
  useEffect(() => {
    if (apiPatients) {
      savePatients(apiPatients);
    }
  }, [apiPatients]);

  // Load from offline storage
  const [patients, setPatients] = useState<Patient[]>([]);

  useEffect(() => {
    const loadOfflineData = async () => {
      const offlinePatients = await db.patients.toArray();
      if (offlinePatients.length > 0) {
        setPatients(offlinePatients);
      }
    };
    loadOfflineData();
  }, []);

  // Update display when online data arrives
  useEffect(() => {
    if (apiPatients) {
      setPatients(apiPatients);
    }
  }, [apiPatients]);

  // WebSocket real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handlePatientUpdate = async (updatedPatient: Patient) => {
      // Update IndexedDB
      await db.patients.put(updatedPatient);
      
      // Update UI
      setPatients(prev => {
        const index = prev.findIndex(p => p.id === updatedPatient.id);
        if (index >= 0) {
          const newPatients = [...prev];
          newPatients[index] = updatedPatient;
          return newPatients;
        } else {
          return [...prev, updatedPatient];
        }
      });
      
      toast.success(`Patient ${updatedPatient.name} updated`, { duration: 2000 });
    };

    socket.on('patient:updated', handlePatientUpdate);

    return () => {
      socket.off('patient:updated', handlePatientUpdate);
    };
  }, [socket, isConnected]);

  // Sync when coming back online
  useEffect(() => {
    if (isOnline) {
      syncManager.syncPendingChanges().then(({ success, failed }) => {
        if (success > 0) {
          toast.success(`Synced ${success} changes`);
          refetch(); // Refresh data after sync
        }
        if (failed > 0) {
          toast.error(`Failed to sync ${failed} changes`);
        }
      });
    }
  }, [isOnline, refetch]);

  // Pull to refresh handler
  const handleRefresh = async () => {
    if (!isOnline) {
      toast.error('Cannot refresh while offline');
      return;
    }
    
    setIsRefreshing(true);
    try {
      await refetch();
      toast.success('Data refreshed');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Calculate stats
  const highPriority = patients.filter(p => p.priority === 'High').length;
  const mediumPriority = patients.filter(p => p.priority === 'Medium').length;

  const stats = [
    {
      title: 'Total Patients',
      value: patients.length,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      color: 'peacock' as const,
    },
    {
      title: 'High Priority',
      value: highPriority,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      color: 'critical' as const,
    },
    {
      title: 'Medium Priority',
      value: mediumPriority,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'amber' as const,
    },
  ];

  return (
    <div className="page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Priority Patients</h1>
          <p className="text-gray-600 mt-1">Monitor and manage high-priority patient care</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={!isOnline || isRefreshing}
          className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRefreshing ? (
            <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Refresh
        </button>
      </div>

      {/* Quick Stats */}
      {patients.length > 0 && <QuickStats stats={stats} />}

      {!isOnline && patients.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-yellow-800">
            You're offline and no cached data is available. Patient data will load when connection is restored.
          </p>
        </div>
      )}

      {isLoading && patients.length === 0 ? (
        <SkeletonList count={5} type="patient" />
      ) : patients.length === 0 ? (
        <EmptyState
          icon="patients"
          title="No patients found"
          description="There are no priority patients at the moment. Patient data will appear here when available."
          action={isOnline ? {
            label: 'Refresh Data',
            onClick: handleRefresh
          } : undefined}
        />
      ) : (
        <div className="space-y-3">
          {patients.map((p) => (
            <PatientCard key={p.id} patient={p} />
          ))}
        </div>
      )}
    </div>
  );
}

